import React from 'react';
import { Download, Upload, Plus, FolderTree } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthButton } from '@/core/permissions/AuthButton';
import { DataFilterToolbar, FilterDefinition } from '@/components/ui/DataFilterToolbar';
import type { InventoryStatus, InventoryFilters } from '../types/inventoryList.types';

interface InventoryToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: 'all' | InventoryStatus;
  onStatusChange: (status: 'all' | InventoryStatus) => void;
  filters: InventoryFilters;
  onFilterChange: (key: keyof InventoryFilters, value: any) => void;
  filterOptions?: {
    categories: any[];
    warehouses: string[];
    suppliers: string[];
  };
  onExport?: () => void;
  onAdd?: () => void;
}

export function InventoryToolbar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  filters,
  onFilterChange,
  filterOptions = { categories: [], warehouses: [], suppliers: [] },
  onExport,
  onAdd,
}: InventoryToolbarProps) {
  const navigate = useNavigate();

  const filterDefs: FilterDefinition[] = [
    {
      id: 'category',
      label: 'Tất cả danh mục',
      options: filterOptions.categories.length > 0 && typeof filterOptions.categories[0] === 'object'
        ? filterOptions.categories
        : filterOptions.categories.map(c => ({ label: c, value: c }))
    },
    {
      id: 'warehouse',
      label: 'Tất cả kho',
      options: filterOptions.warehouses.map(w => ({ label: w, value: w }))
    },
    {
      id: 'status',
      label: 'Tất cả trạng thái',
      options: [
        { label: 'Còn hàng', value: 'in_stock' },
        { label: 'Sắp hết hàng', value: 'low_stock' },
        { label: 'Hết hàng', value: 'out_of_stock' },
        { label: 'Ngưng sử dụng', value: 'inactive' },
      ]
    },
    {
      id: 'itemType',
      label: 'Tất cả loại',
      options: [
        { label: 'Vật tư tiêu hao', value: 'Vật tư tiêu hao' },
        { label: 'Vật tư cố định', value: 'Vật tư cố định' },
        { label: 'Vật tư thay thế', value: 'Vật tư thay thế' },
      ]
    },
    {
      id: 'supplier',
      label: 'Tất cả NCC',
      options: filterOptions.suppliers.map(s => ({ label: s, value: s }))
    },
    {
      id: 'stockStatus',
      label: 'Tất cả tồn kho',
      options: [
        { label: 'Tồn > 100', value: 'Tồn > 100' },
        { label: 'Tồn thấp', value: 'Tồn thấp' },
        { label: 'Tồn âm', value: 'Tồn âm' },
        { label: 'Không phát sinh', value: 'Không phát sinh' },
      ]
    }
  ];

  const filterValues = {
    ...filters,
    status: status === 'all' ? '' : status
  };

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'status') {
      onStatusChange(value ? (value as InventoryStatus) : 'all');
    } else {
      onFilterChange(key as keyof InventoryFilters, value);
    }
  };

  const actions = (
    <>
      <AuthButton 
        module="inventory.transactions"
        action="create"
        behavior="hide"
        className="h-9 px-3 flex items-center gap-2 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors shrink-0 shadow-sm"
      >
        <Upload className="w-4 h-4 text-emerald-600" />
        <span className="whitespace-nowrap">Nhập kho</span>
      </AuthButton>

      <AuthButton
        module="inventory.transactions"
        action="create"
        behavior="hide"
        onClick={onExport}
        className="h-9 px-3 flex items-center gap-2 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors shrink-0 shadow-sm"
      >
        <Download className="w-4 h-4 text-amber-600" />
        <span className="whitespace-nowrap">Xuất kho</span>
      </AuthButton>

      <button
        onClick={() => navigate('/inventory/categories')}
        className="h-9 px-3 hidden lg:flex items-center gap-2 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors shrink-0 shadow-sm"
      >
        <FolderTree className="w-4 h-4 text-amber-600" />
        <span className="whitespace-nowrap">Nhóm vật tư</span>
      </button>

      {onAdd && (
        <AuthButton
          module="inventory.items"
          action="create"
          behavior="disable"
          onClick={onAdd}
          className="h-9 px-3 flex items-center gap-2 rounded-md bg-emerald-600 text-xs font-medium text-white hover:bg-emerald-700 transition-all border-none shrink-0 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span className="whitespace-nowrap">Thêm vật tư</span>
        </AuthButton>
      )}
    </>
  );

  return (
    <DataFilterToolbar
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder="Tìm mã, tên, danh mục..."
      filterDefs={filterDefs}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      actions={actions}
    />
  );
}
